import { Component, effect, inject, input, model } from '@angular/core';
import {
  FormGroup,
  FormControl,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  NgSelectComponent,
  NgLabelTemplateDirective,
  NgOptionTemplateDirective,
} from '@ng-select/ng-select';

import { CrudService } from '../../core/services/crud.service';
import { removeEmptyValues } from '../../shared/helpers';
import { RollForm } from '../../core/services/crud.interface';
import { ModalComponent } from '../../shared/components/modal/modal.component';
import { ColorTotalComponent } from '../../shared/components/color-total/color-total.component';

@Component({
  selector: 'app-roll-form',
  imports: [
    ReactiveFormsModule,
    NgSelectComponent,
    NgLabelTemplateDirective,
    NgOptionTemplateDirective,
    ModalComponent,
    ColorTotalComponent,
  ],
  templateUrl: './roll-form.component.html',
  styles: ``,
})
export class RollFormComponent {
  private crudService = inject(CrudService);

  showModal = model(false);

  existingRoll = model();

  rollForm = new FormGroup({
    product_id: new FormControl<string | undefined | null>(
      null,
      Validators.required
    ),
    supplier_id: new FormControl(null, Validators.required),
    warehouse_id: new FormControl(null, Validators.required),
    color: new FormControl(null, Validators.required), //enum REQ --> custom control
    roll_identifier: new FormControl('', Validators.required),
    initial_length_meters: new FormControl(0, [
      Validators.required,
      Validators.min(0),
    ]),
    initial_weight_kg: new FormControl(0, [
      Validators.required,
      Validators.min(0),
    ]),
    width: new FormControl(0, [Validators.required, Validators.min(0)]),
    // units: new FormControl(1, [Validators.required, Validators.min(1)]),
    purchase_cost: new FormControl(0, [Validators.required, Validators.min(0)]),
    purchase_cost_uom: new FormControl(null, Validators.required),
    purchase_reference: new FormControl(''),
  });

  confirmationMessage = '';
  errorMessage = '';

  products: Array<any> = [];
  suppliers: Array<any> = [];
  warehouses: Array<any> = [];
  colors: Array<any> = [];
  uoms: Array<any> = [];

  productId = input<string>();

  buttonText = '';

  constructor() {
    effect(() => {
      if (this.showModal()) {
        this.initializeCombos();

        if (this.existingRoll() !== null) {
          this.buttonText = 'Actualizar';

          this.rollForm.patchValue({
            product_id: (this.existingRoll() as any)['product_id'],
            supplier_id: (this.existingRoll() as any)['supplier_id'],
            warehouse_id: (this.existingRoll() as any)['warehouse_id'],
            color: (this.existingRoll() as any)['color'],
            roll_identifier: (this.existingRoll() as any)['roll_identifier'],
            initial_length_meters: (this.existingRoll() as any)[
              'initial_length_meters'
            ],
            initial_weight_kg: (this.existingRoll() as any)[
              'initial_weight_kg'
            ],
            width: (this.existingRoll() as any)['width'],
            // units: 1,
            purchase_cost: (this.existingRoll() as any)['purchase_cost'],
            purchase_cost_uom: (this.existingRoll() as any)[
              'purchase_cost_uom_key'
            ],
            purchase_reference: (this.existingRoll() as any)[
              'purchase_reference'
            ],
          });
        } else {
          this.buttonText = 'Crear';

          this.generateRollIdentifier();
        }
      }
    });
  }

  initializeCombos() {
    this.crudService.getProducts().subscribe({
      next: (response: any) => {
        if (response.ok) {
          this.products = response.body;

          if (this.productId()) {
            this.rollForm.get('product_id')?.setValue(this.productId());
          }
        }
      },
    });

    this.crudService.getSuppliers().subscribe({
      next: (response: any) => {
        if (response.ok) {
          this.suppliers = response.body;
        }
      },
    });

    this.crudService.getWarehouses().subscribe({
      next: (response: any) => {
        if (response.ok) {
          this.warehouses = response.body;
        }
      },
    });

    this.crudService.getEnum('Color').subscribe({
      next: (response: any) => {
        if (response.ok) {
          this.colors = Object.entries(response.body).map(([key, value]) => {
            return {
              id: key,
              name: (value as Array<any>)[0],
            };
          });
        }
      },
    });

    this.crudService.getEnum('UnitOfMeasure').subscribe({
      next: (response: any) => {
        if (response.ok) {
          this.uoms = Object.entries(response.body).map(([key, value]) => {
            return { id: key, name: value };
          });
        }
      },
    });
  }

  clearForm() {
    this.rollForm.reset({
      product_id: null,
      supplier_id: null,
      warehouse_id: null,
      color: null,
      roll_identifier: '',
      initial_length_meters: 0,
      initial_weight_kg: 0,
      width: 0,
      // units: 1,
      purchase_cost: 0,
      purchase_cost_uom: null,
      purchase_reference: '',
    });
    this.existingRoll.set(null);

    this.generateRollIdentifier();
  }

  submit() {
    const cleanRollForm = removeEmptyValues(this.rollForm.value);

    this.confirmationMessage = '';
    this.errorMessage = '';

    if (this.existingRoll() != null) {
      this.updateRoll(this.rollForm.value as RollForm);
    } else {
      this.createRoll(cleanRollForm as RollForm);
    }
  }

  createRoll(cleanRollForm: RollForm) {
    this.crudService.createProductRoll(cleanRollForm as RollForm).subscribe({
      next: (response: any) => {
        if (response.status === 201) {
          this.confirmationMessage = 'Rollo añadido';

          setTimeout(() => {
            this.confirmationMessage = '';
          }, 3000);
        }
      },
      error: (error: any) => {
        if (error.status === 409) {
          this.errorMessage = error.error.message;
        } else {
          this.errorMessage = 'Error inesperado. Intente nuevamente.';
        }

        setTimeout(() => {
          this.errorMessage = '';
        }, 3000);
      },
      complete: () => {
        this.generateRollIdentifier();
      },
    });
  }

  updateRoll(rollForm: RollForm) {
    this.crudService
      .updateProductRoll((this.existingRoll() as any)['id'], rollForm)
      .subscribe({
        next: (response: any) => {
          if (response.ok) {
            this.close();
          }
        },
      });
  }

  cancel() {
    this.close();
  }

  close() {
    this.clearForm();
    this.showModal.set(false);
  }

  generateRollIdentifier() {
    this.rollForm
      .get('roll_identifier')
      ?.setValue(`ROLL-${Date.now().toString()}`);
  }
}
